import { useEffect, useState } from 'react';
import { timeZone } from '../../lib/time';
import { saveReminderPrefs, loadReminderPrefs } from '../../telegram/prefs';
import type { ReminderPrefs } from '@sayfazkari/shared';

const STORAGE_KEY = 'sa_reminders_v1';

function readLocal(): ReminderPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ReminderPrefs;
  } catch {
    /* повреждённые данные — начинаем с чистого состояния */
  }
  return { tz: timeZone(), morningTime: null, eveningTime: null };
}

function writeLocal(p: ReminderPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* приватный режим — настройки просто не переживут перезаход */
  }
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Источник правды — база бота: именно оттуда уходят напоминания.
 * Локальная копия существует только чтобы поле было заполнено сразу,
 * не дожидаясь сети.
 *
 * Раньше запись в localStorage стояла ПОСЛЕ запроса к боту, и при
 * любой сетевой ошибке введённое время пропадало: поле оказывалось
 * пустым, а нативный пикер показывал текущее время. Теперь локальная
 * запись выполняется сразу и от сети не зависит.
 */
export function useReminderPrefs() {
  const [prefs, setPrefs] = useState<ReminderPrefs>(readLocal);
  const [status, setStatus] = useState<SaveStatus>('idle');

  // Сервер главнее локальной копии: она могла устареть или потеряться
  // при чистке данных, тогда как напоминания продолжают приходить.
  useEffect(() => {
    let cancelled = false;
    loadReminderPrefs()
      .then((remote) => {
        if (cancelled || !remote) return;
        setPrefs(remote);
        writeLocal(remote);
      })
      .catch((e) => {
        console.warn('[SayfAzkari] не удалось прочитать напоминания', e);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function update(patch: Partial<ReminderPrefs>) {
    setPrefs((p) => {
      const next = { ...p, ...patch };
      // Пишем немедленно: ввод пользователя не должен зависеть
      // от того, доступен ли сейчас бот
      writeLocal(next);
      return next;
    });
    setStatus('idle');
  }

  async function save() {
    setStatus('saving');
    writeLocal(prefs);
    try {
      await saveReminderPrefs(prefs);
      setStatus('saved');
    } catch (e) {
      console.warn('[SayfAzkari] не удалось сохранить напоминания', e);
      setStatus('error');
    }
  }

  return { prefs, update, save, status };
}
